import { Button, useTranslation } from '@hbs-us/ui';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import type { FleetMaintenanceCategory, FleetVehicleDto } from './contractsMirror.js';
import {
  type FleetEntryInput,
  useCreateFleetEntryMutation,
  useUpdateFleetEntryMutation,
} from './useFleetQuery.js';

/**
 * A focus-trapped drawer (mirrors the monthlyBills AddBillDrawer a11y pattern) for logging or
 * editing a fuel/maintenance fleet entry with an optional receipt file (F12/F13). Escape closes;
 * Tab wraps focus; focus returns to the trigger on close. The receipt is sent multipart — the
 * mutation builds FormData so the browser sets the boundary.
 */

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';
const INPUT_CLASS =
  'h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const LABEL_CLASS = 'mb-1 block font-medium text-foreground text-sm';

const MAINTENANCE_CATEGORIES: readonly FleetMaintenanceCategory[] = [
  'oil_change',
  'tire_rotation',
  'inspection',
  'brakes',
  'tires',
  'engine',
  'transmission',
  'cooling',
  'electrical',
  'other',
];

export interface EntryDraft {
  id: number;
  vehicleId: number;
  entryType: 'fuel' | 'maintenance';
  entryDate: string;
  vendor: string | null;
  amount: number;
  odometer: number | null;
  maintenanceCategory: FleetMaintenanceCategory | null;
  hasReceipt: boolean;
}

export interface EntryFormDrawerProps {
  open: boolean;
  mode: 'create' | 'edit';
  vehicles: FleetVehicleDto[];
  defaultVehicleId?: number | null;
  entry?: EntryDraft | null;
  onClose: () => void;
}

export function EntryFormDrawer({
  open,
  mode,
  vehicles,
  defaultVehicleId,
  entry,
  onClose,
}: EntryFormDrawerProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const createMutation = useCreateFleetEntryMutation();
  const updateMutation = useUpdateFleetEntryMutation(entry?.id ?? 0);
  const isEdit = mode === 'edit';
  const mutation = isEdit ? updateMutation : createMutation;
  const [error, setError] = useState<string | null>(null);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) return null;
  if (isEdit && !entry) return null;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    const amount = Number(form.get('amount'));
    if (!Number.isFinite(amount) || amount < 0) {
      setError(t('fleet:entryForm.invalidAmount'));
      return;
    }
    const entryType =
      String(form.get('entry_type') ?? 'fuel') === 'maintenance' ? 'maintenance' : 'fuel';
    const odometerRaw = String(form.get('odometer') ?? '').trim();
    const gallonsRaw = String(form.get('gallons') ?? '').trim();
    const receiptFile = form.get('receipt');

    const input: FleetEntryInput = {
      vehicle_id: Number(form.get('vehicle_id')),
      entry_type: entryType,
      entry_date: String(form.get('entry_date') ?? '').trim(),
      vendor: String(form.get('vendor') ?? '').trim() || null,
      amount,
      odometer: odometerRaw ? Number(odometerRaw) : null,
      gallons: gallonsRaw ? Number(gallonsRaw) : null,
      maintenance_category:
        entryType === 'maintenance' ? String(form.get('maintenance_category') ?? 'other') : null,
      notes: String(form.get('notes') ?? '').trim() || null,
      receipt: receiptFile instanceof File && receiptFile.size > 0 ? receiptFile : null,
      remove_receipt: String(form.get('remove_receipt') ?? '') === 'on',
    };

    mutation.mutate(input, {
      onSuccess: () => onClose(),
      onError: (err) => setError(err.message),
    });
  };

  const defaultVehicle = entry?.vehicleId ?? defaultVehicleId ?? vehicles[0]?.id ?? 0;
  const defaultType = entry?.entryType ?? 'fuel';
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-foreground/40">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onKeyDown={handleKeyDown}
        className="h-full w-full max-w-md overflow-y-auto border-border border-l bg-card p-6 shadow-lg"
      >
        <h2 id={headingId} className="font-display font-semibold text-foreground text-xl">
          {isEdit ? t('fleet:entryForm.editHeading') : t('fleet:entryForm.createHeading')}
        </h2>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          {error ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
              {error}
            </p>
          ) : null}

          <div>
            <label htmlFor="entry-vehicle" className={LABEL_CLASS}>
              {t('fleet:entryForm.vehicle')}
            </label>
            <select
              id="entry-vehicle"
              name="vehicle_id"
              required
              defaultValue={String(defaultVehicle)}
              className={INPUT_CLASS}
            >
              {vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.displayName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="entry-type" className={LABEL_CLASS}>
              {t('fleet:entryForm.type')}
            </label>
            <select
              id="entry-type"
              name="entry_type"
              defaultValue={defaultType}
              className={INPUT_CLASS}
            >
              <option value="fuel">{t('fleet:entryType.fuel')}</option>
              <option value="maintenance">{t('fleet:entryType.maintenance')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="entry-category" className={LABEL_CLASS}>
              {t('fleet:entryForm.category')}
            </label>
            <select
              id="entry-category"
              name="maintenance_category"
              defaultValue={entry?.maintenanceCategory ?? 'other'}
              className={INPUT_CLASS}
            >
              {MAINTENANCE_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {t(`fleet:maintenanceCategory.${category}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="entry-date" className={LABEL_CLASS}>
              {t('fleet:entryForm.date')}
            </label>
            <input
              id="entry-date"
              name="entry_date"
              type="date"
              required
              defaultValue={entry?.entryDate ?? today}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="entry-amount" className={LABEL_CLASS}>
              {t('fleet:entryForm.amount')}
            </label>
            <input
              id="entry-amount"
              name="amount"
              type="number"
              step="0.01"
              min="0"
              required
              defaultValue={entry ? String(entry.amount) : ''}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="entry-vendor" className={LABEL_CLASS}>
              {t('fleet:entryForm.vendor')}
            </label>
            <input
              id="entry-vendor"
              name="vendor"
              defaultValue={entry?.vendor ?? ''}
              className={INPUT_CLASS}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="entry-odometer" className={LABEL_CLASS}>
                {t('fleet:entryForm.odometer')}
              </label>
              <input
                id="entry-odometer"
                name="odometer"
                type="number"
                step="1"
                min="0"
                defaultValue={entry?.odometer != null ? String(entry.odometer) : ''}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label htmlFor="entry-gallons" className={LABEL_CLASS}>
                {t('fleet:entryForm.gallons')}
              </label>
              <input
                id="entry-gallons"
                name="gallons"
                type="number"
                step="0.001"
                min="0"
                className={INPUT_CLASS}
              />
            </div>
          </div>

          <div>
            <label htmlFor="entry-receipt" className={LABEL_CLASS}>
              {t('fleet:entryForm.receipt')}
            </label>
            {/*
              The tappable part of a file input is its BUTTON, not the element box, and the
              unstyled button is only 20px tall — under the 24px WCAG 2.5.8 target floor.
              The `file:` utilities size and skin that button to match the drawer's other
              controls; the input itself is untouched, so the multipart submit is unchanged.
            */}
            <input
              id="entry-receipt"
              name="receipt"
              type="file"
              accept="image/*,.pdf"
              className="block w-full text-foreground text-sm file:mr-3 file:h-9 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:font-medium file:text-foreground file:text-sm"
            />
          </div>

          {isEdit && entry?.hasReceipt ? (
            <label className="flex items-center gap-2 font-medium text-foreground text-sm">
              <input
                type="checkbox"
                name="remove_receipt"
                className="size-4 rounded border-border"
              />
              {t('fleet:entryForm.removeReceipt')}
            </label>
          ) : null}

          <div>
            <label htmlFor="entry-notes" className={LABEL_CLASS}>
              {t('fleet:entryForm.notes')}
            </label>
            <textarea
              id="entry-notes"
              name="notes"
              rows={3}
              defaultValue={''}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button type="submit" disabled={mutation.isPending}>
              {isEdit ? t('fleet:entryForm.save') : t('fleet:entryForm.log')}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('fleet:entryForm.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
